import { WarningCircleIcon } from "@phosphor-icons/react/WarningCircle";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import type { AddLineItemType } from "@/Shared/Types/lineItemTypes";
import type { AddLocationLocationType } from "@/Shared/Types/locationTypes";
import { formatPrice } from "@/Helpers/formatPrice";
import { priceBeforeTax } from "@/Helpers/priceBeforeTax";
import { symbolForCurrency } from "@/Helpers/symbolForCurrency";
import { isDomesticCanada } from "@/Helpers/isDomesticCanada";
import type { LineItem } from "@freightsimple/generated-dashboard-openapi-client/models/LineItem";
import type { Location } from "@freightsimple/generated-dashboard-openapi-client/models/Location";
import type { PreQuoteLocation } from "@freightsimple/generated-dashboard-openapi-client/models/PreQuoteLocation";
import type { Quote } from "@freightsimple/generated-dashboard-openapi-client/models/Quote";
import type { StateOfGoods } from "@freightsimple/generated-dashboard-openapi-client/models/StateOfGoods";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import type { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";
import moment from "moment";
import QuotesDeliveryColumn from "./QuotesDeliveryColumn";
import QuotesPickupColumn from "./QuotesPickupColumn";
import QuotesProgress from "./QuotesProgress";
import QuotesTransitColumn from "./QuotesTransitColumn";
import QuotesBeforeCouponTag from "./QuotesBeforeCouponTag";

interface QuoteRowBodyProps {
  pickupDate: moment.Moment;
  pickupDeadline: string | undefined;
  pickupLocation: Location | AddLocationLocationType | PreQuoteLocation;
  lineItems: Array<LineItem | AddLineItemType>;
  deliveryLocation: Location | AddLocationLocationType | PreQuoteLocation;
  addInsuranceToShipment: boolean;
  insuranceAmount: number;
  insuranceCurrency: Currency;
  stateOfGoods: StateOfGoods;
  deliveryDeadline: string | undefined;
  quote: Quote;
  onSelectQuote: () => void;
  notes: string | undefined;
  priceBeforeCoupon?: number | undefined;
  pickupReferenceNumber?: string | undefined;
  deliveryReferenceNumber?: string | undefined;
  deliveryAmazonReferenceId?: string | undefined;
  deliveryAmazonShipmentId?: string | undefined;
}

// The large headline price: a raised currency symbol, a 48px amount (colour
// inherited — the original's `color:"#545454"` was quoted, hence ignored), and
// a currency label dropped toward the baseline.
function LargePrice_({ currency, price }: { currency: string; price: number }) {
  return (
    <div className="flex flex-row items-start justify-end">
      <div className="relative top-[2px] text-[24px] leading-none font-semibold text-foreground">
        {symbolForCurrency(currency)}
      </div>
      <div className="text-[48px] leading-none font-semibold">
        {formatPrice(price)}
      </div>
      <div className="relative top-[20px] ml-[2px] text-[24px] leading-none font-semibold text-content-muted">
        {currency}
      </div>
    </div>
  );
}

function QuotesRowBody(props: QuoteRowBodyProps) {
  const {
    quote,
    pickupLocation,
    deliveryLocation,
    pickupDate,
    lineItems,
    pickupDeadline,
  } = props;
  const serviceType = quote.serviceDisplayName;

  const expectedDeliveryDateLabel = "Earliest Delivery";

  const expectedDeliveryDateMoment = quote.expectedDeliveryDate
    ? moment(quote.expectedDeliveryDate)
    : undefined;

  const formattedDeliveryDate =
    expectedDeliveryDateMoment?.format("dddd Do MMMM YYYY") || "Unknown";

  function describeTransitDays(
    earliestTransitDays: number | undefined,
    latestTransitDays: number | undefined,
  ) {
    if (earliestTransitDays === undefined) {
      return undefined;
    } else {
      if (
        latestTransitDays === undefined ||
        latestTransitDays === earliestTransitDays
      ) {
        if (earliestTransitDays === 0) {
          return quote.transitBusinessDaysDescription ?? "Same Day";
        }
        if (earliestTransitDays === 1) {
          return `${earliestTransitDays} Business Day`;
        } else {
          return `${earliestTransitDays} Business Days`;
        }
      } else {
        return `${earliestTransitDays}-${latestTransitDays} Business Days`;
      }
    }
  }

  const transitDaysDescription = describeTransitDays(
    quote.transitBusinessDays,
    quote.latestTransitBusinessDays,
  );
  const transitDaysLabel = transitDaysDescription
    ? `: ${transitDaysDescription}`
    : "";

  const restrictions = quote.restrictions ?? [];

  function taxDescription() {
    if (quote.taxAmount === 0) {
      return `Inclusive of all taxes`;
    } else {
      const afterTax = formatPrice(quote.price);
      return `Total after tax : $${afterTax} ${quote.currency}`;
    }
  }

  return (
    <div className="flex w-full flex-col items-start">
      <div className="flex w-full flex-row flex-wrap items-start justify-between gap-y-4">
        <div className="flex flex-col items-start">
          <div className="text-[24px] font-semibold text-foreground">
            {serviceType} {transitDaysLabel}
          </div>
          <div className="text-[18px] font-normal text-content-muted">
            {expectedDeliveryDateLabel}: {formattedDeliveryDate}
          </div>
          <div className="flex flex-col items-start">
            {props.quote.notes && restrictions.length === 0 && (
              <div className="mt-2 text-[18px] text-foreground">
                {quote.notes}
              </div>
            )}
            {restrictions.length > 0 && (
              <>
                <div className="mt-6 flex flex-row gap-[4px]">
                  <div className="text-[18px] font-medium text-foreground">
                    Restrictions Apply
                  </div>
                  <WarningCircleIcon
                    className="mb-[-4px] h-4 w-4 text-gold"
                    weight="fill"
                  />
                </div>
                {/* Text styles live on the li so the bullet marker matches
                 * the text instead of floating at the inherited black. */}
                <ul className="m-0 pl-5">
                  {restrictions.map((r) => (
                    <li
                      key={r.description}
                      className="text-[16px] text-content-muted"
                    >
                      {r.description}
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </div>
        {/* pr-[3px] keeps the Select Quote button off the RevealBox's
         * overflow:hidden right edge, so its hover scale isn't clipped. */}
        <div className="flex flex-col items-end pr-[3px]">
          <LargePrice_
            price={priceBeforeTax(props.quote.price, props.quote.taxAmount)}
            currency={props.quote.currency}
          />
          <div className="h-1" />
          <div className="text-right text-[14px] leading-none font-light text-foreground">
            {taxDescription()}
          </div>
          {/* priceBeforeCoupon is the original tax-inclusive total, so it sits
           * with the after-tax line — the saving equals the coupon value. */}
          {props.priceBeforeCoupon && (
            <>
              <div className="h-1" />
              <QuotesBeforeCouponTag
                priceBeforeCoupon={props.priceBeforeCoupon}
                size="large"
              />
            </>
          )}
          <div className="h-4" />
          <Button
            primary
            size="large"
            onClick={props.onSelectQuote}
            data-testid="select-quote-button"
          >
            Select Quote
          </Button>
        </div>
      </div>
      {/* The progress bar only reads horizontally, so it goes away when the
       * columns stack. */}
      <div className="w-full in-[.quote-card-narrow]:hidden">
        <div className="h-14" />
        <QuotesProgress shipmentState={ShipmentState.Quoted} />
        <div className="h-4" />
      </div>
      <div className="flex w-full flex-row items-start in-[.quote-card-narrow]:mt-8 in-[.quote-card-narrow]:flex-col in-[.quote-card-narrow]:gap-8">
        <QuotesPickupColumn
          pickupDeadline={pickupDeadline}
          lineItems={lineItems}
          pickupDate={pickupDate}
          pickupLocation={pickupLocation}
          pickupContact={undefined}
          quote={quote}
          shipmentState={ShipmentState.Quoted}
          isDomesticCanada={isDomesticCanada(pickupLocation, deliveryLocation)}
          showNotesSection={true}
          pickupReferenceNumber={props.pickupReferenceNumber}
        />
        <QuotesTransitColumn
          quote={quote}
          shipmentState={ShipmentState.Quoted}
          proNumber={undefined}
          billOfLadingNumber={undefined}
          broker={undefined}
          addInsuranceToShipment={props.addInsuranceToShipment}
          insuranceAmount={props.insuranceAmount}
          insuranceCurrency={props.insuranceCurrency}
          stateOfGoods={props.stateOfGoods}
          priceBeforeCoupon={props.priceBeforeCoupon}
        />
        <QuotesDeliveryColumn
          quote={quote}
          deliveryHours={undefined}
          appointmentDate={undefined}
          deliveryLocation={deliveryLocation}
          deliveryContact={undefined}
          shipmentState={ShipmentState.Quoted}
          actualDeliveryDate={undefined}
          actualDeliveryTime={undefined}
          deliveryDeadline={props.deliveryDeadline}
          expectedDeliveryDate={quote.expectedDeliveryDate}
          latestExpectedDeliveryDate={quote.latestEstimatedDeliveryDate}
          showNotesSection={true}
          deliveryReferenceNumber={props.deliveryReferenceNumber}
          deliveryAmazonReferenceId={props.deliveryAmazonReferenceId}
          deliveryAmazonShipmentId={props.deliveryAmazonShipmentId}
        />
      </div>
    </div>
  );
}
export default QuotesRowBody;
